'use client';

import React from 'react';
import { useMouseClickStates } from '@/hooks/useMouseClickStates';
import { useBookmarkStore } from '@/store/useBookmarkStore';
import { IconButton } from '@/components/base/IconButton';
import { ARIA_LABEL_NAVIGATION_BOOKMARKS } from '@/shared/constants';
import {
  MATOMO_ACTIONS,
  MATOMO_CATEGORIES,
  MATOMO_CONTENT_INTERACTIONS,
  MATOMO_NAMES,
  trackContentInteraction,
  trackMatomoEvent,
} from '@/utils/matomo';
import { useContentBlock } from '@/components/base/ContentTracking';
import styles from './ActionFavorite.module.scss';

// e.g. "Bookmark Event Speichern Klick"
function bookmarkName(bookmarkType: string, suffix: string): string {
  return `${MATOMO_NAMES.BOOKMARK_PREFIX} ${bookmarkType} ${suffix}`;
}

export type FavoriteButtonProps = {
  id: string;
  className?: string;
  bookmarkType?: 'Guide' | 'Event';
  disableBackgroundColor?: boolean;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'>;

function ActionFavorite({
  className,
  id,
  bookmarkType,
  disableBackgroundColor = false,
  ...rest
}: FavoriteButtonProps) {
  const [itemRef] = useMouseClickStates();

  // Set when the button sits inside a tracked card — see ContentTracking.
  const contentBlock = useContentBlock();

  const { bookmarks, addBookmark, removeBookmark, _hasHydrated } = useBookmarkStore();

  const isBookmarked = _hasHydrated && bookmarks.some((bookmark) => bookmark.id === id);

  const handleToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (isBookmarked) {
      removeBookmark(id);
      trackMatomoEvent(
        MATOMO_CATEGORIES.INTERACTIONS,
        MATOMO_ACTIONS.BOOKMARK_CLICK,
        bookmarkType && bookmarkName(bookmarkType, MATOMO_NAMES.BOOKMARK_REMOVE_SUFFIX),
      );
    } else {
      const type =
        bookmarkType === 'Event'
          ? 'event'
          : bookmarkType === 'Guide'
            ? 'article'
            : undefined;
      addBookmark(id, type);

      trackMatomoEvent(
        MATOMO_CATEGORIES.INTERACTIONS,
        MATOMO_ACTIONS.BOOKMARK_CLICK,
        bookmarkType && bookmarkName(bookmarkType, MATOMO_NAMES.BOOKMARK_ADD_SUFFIX),
      );

      // Only saving counts as a content interaction, removing does not.
      if (contentBlock) {
        trackContentInteraction(MATOMO_CONTENT_INTERACTIONS.BOOKMARK, contentBlock);
      }
    }
  };

  return !_hasHydrated ? (
    <></>
  ) : (
    <IconButton
      ref={itemRef}
      src={isBookmarked ? '/icons/icon-bookmark-filled.svg' : '/icons/icon-bookmark.svg'}
      onClick={handleToggle}
      alt={ARIA_LABEL_NAVIGATION_BOOKMARKS}
      outerClassName={[styles.actionFavorite, className].filter(Boolean).join(' ')}
      innerClassName={[
        styles.inner,
        disableBackgroundColor ? undefined : styles.innerWithBackground,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    ></IconButton>
  );
}

export default ActionFavorite;
